import Janus from 'janus-gateway';
import { parseMetadata } from './stream-metadata.js';

/**
 * When a stethoscope detail panel is open we poll *only* that mountpoint via
 * Janus Streaming's `info` request at this cadence. 200 ms gives near-live
 * updates for heart-rate / placement metadata while keeping the wire chatter
 * to a single small request per tick.
 */
export const FOCUSED_POLL_MS = 200;

/**
 * @typedef {Object} StreamMountpoint
 * @property {number} id
 * @property {string} description
 * @property {string} type
 * @property {boolean} enabled
 * @property {ReturnType<typeof parseMetadata>} metadata
 * @property {string | null} metadataRaw
 */

let janusLibraryReady = false;

function ensureJanusLibrary (onReady, onError) {
	if (janusLibraryReady) {
		onReady();
		return;
	}
	if (!Janus.isWebrtcSupported()) {
		onError('WebRTC is not supported in this browser.');
		return;
	}
	Janus.init({
		debug: process.env.NODE_ENV === 'development' ? 'all' : false,
		callback: () => {
			janusLibraryReady = true;
			onReady();
		},
	});
}

/**
 * Thin wrapper around the Janus Streaming plugin API that exposes a
 * callback-based surface for the Vue layer.
 */
export class StreamingApp {
	/**
	 * @param {(streams: StreamMountpoint[]) => void} onList
	 * @param {(event: "created" | "destroyed" | "metadata", stream: StreamMountpoint) => void} onLifecycle
	 * @param {(connected: boolean) => void} onConnection
	 */
	constructor (onList, onLifecycle, onConnection) {
		this.onList = onList;
		this.onLifecycle = onLifecycle;
		this.onConnection = onConnection;

		this.janus = null;
		this.listHandle = null;
		this.watchHandle = null;
		this.listPollTimer = null;
		this.focusedPollTimer = null;
		this.focusedPollId = null;
		this.backgroundPollMs = null;
		/** @type {Map<number, StreamMountpoint>} */
		this.knownIds = new Map();
		this.remoteAudio = null;
		this.levelCanvas = null;
		this.pendingStream = null;
		this.audioCtx = null;
		this.analyser = null;
		this.meterRaf = 0;
		this.watchingId = null;
	}

	connect (serverUrl, pollIntervalMs) {
		this.disconnect();
		ensureJanusLibrary(() => this.openSession(serverUrl, pollIntervalMs));
	}

	disconnect () {
		this.stopListPoll();
		this.stopFocusedPoll();

		const handle = this.listHandle;
		this.listHandle = null;
		this.backgroundPollMs = null;

		this.stopWatch();
		this.knownIds.clear();
		this.onList([]);

		if (handle) {
			try {
				handle.detach();
			} catch {
				/* ignore */
			}
		}

		if (this.janus) {
			try {
				this.janus.destroy();
			} catch {
				/* ignore */
			}
			this.janus = null;
		}

		this.closeAudioCtx();
		this.onConnection(false);
	}

	refreshNow () {
		this.refreshList();
	}

	destroyStream (id, onDone) {
		if (!this.listHandle) {
			if (onDone) onDone(false);
			return;
		}
		if (this.watchingId === id) {
			this.stopWatch();
		}
		this.listHandle.send({
			message: { request: 'destroy', id },
			success: (result) => {
				if (result.streaming === 'destroyed') {
					if (onDone) onDone(true);
				} else if (result.error) {
					if (onDone) onDone(false);
				} else {
					if (onDone) onDone(true);
				}
				this.refreshList();
			},
			error: () => {
				if (onDone) onDone(false);
			},
		});
	}

	destroyAll (ids, onDone) {
		if (ids.length === 0) {
			if (onDone) onDone(0, 0);
			return;
		}
		let ok = 0;
		let fail = 0;
		let pending = ids.length;
		for (const id of ids) {
			this.destroyStream(id, (success) => {
				if (success) ok++;
				else fail++;
				pending--;
				if (pending === 0) {
					if (onDone) onDone(ok, fail);
				}
			});
		}
	}

	/**
	 * @param {(s: StreamMountpoint) => boolean} predicate
	 * @param {(streams: StreamMountpoint[]) => void} [onDone]
	 */
	listAndFilter (predicate, onDone) {
		if (!this.listHandle) {
			if (onDone) onDone([]);
			return;
		}
		this.listHandle.send({
			message: { request: 'list' },
			success: (result) => {
				const raw = result && result.list ? result.list : [];
				const all = raw.map(normalizeListItem).filter((s) => s !== null);
				let matched;
				try {
					matched = all.filter(predicate);
				} catch {
					matched = [];
				}
				if (onDone) onDone(matched);
			},
			error: () => {
				if (onDone) onDone([]);
			},
		});
	}

	listByHospitalId (hospitalId, onDone) {
		const wanted = Number(hospitalId);
		if (!Number.isFinite(wanted)) {
			if (onDone) onDone([]);
			return;
		}
		this.listAndFilter((s) => Number(s.metadata && s.metadata.hospital && s.metadata.hospital.id) === wanted, onDone);
	}

	listByPatientId (patientId, onDone) {
		const wanted = Number(patientId);
		if (!Number.isFinite(wanted)) {
			if (onDone) onDone([]);
			return;
		}
		this.listAndFilter((s) => Number(s.metadata && s.metadata.patient && s.metadata.patient.id) === wanted, onDone);
	}

	destroyByHospitalId (hospitalId, onDone) {
		this.listByHospitalId(hospitalId, (streams) => {
			const ids = streams.map((s) => s.id);
			if (ids.length === 0) {
				if (onDone) onDone(0, 0);
				return;
			}
			this.destroyAll(ids, onDone);
		});
	}

	destroyByPatientId (patientId, onDone) {
		this.listByPatientId(patientId, (streams) => {
			const ids = streams.map((s) => s.id);
			if (ids.length === 0) {
				if (onDone) onDone(0, 0);
				return;
			}
			this.destroyAll(ids, onDone);
		});
	}

	watchById (id, pin) {
		if (!this.janus) {
			return;
		}
		const stream = this.knownIds.get(id) || {
			id,
			description: 'Mountpoint ' + id,
			type: 'rtp',
			enabled: true,
			metadata: null,
			metadataRaw: null,
		};
		this.startWatch(stream, pin);
	}

	watchStream (stream, pin) {
		this.startWatch(stream, pin);
	}

	stopWatch () {
		if (this.watchHandle) {
			try {
				this.watchHandle.send({ message: { request: 'stop' } });
				this.watchHandle.hangup();
				this.watchHandle.detach();
			} catch {
				/* ignore */
			}
			this.watchHandle = null;
		}
		this.watchingId = null;
		this.pendingStream = null;
		this.stopFocusedPoll();
		this.stopMeter();
		if (this.remoteAudio) {
			this.remoteAudio.srcObject = null;
		}
		if (this.listHandle && this.backgroundPollMs && !this.listPollTimer) {
			this.startListPoll(this.backgroundPollMs);
		}
	}

	setPlayerElements (audio, canvas) {
		this.remoteAudio = audio;
		this.levelCanvas = canvas;
		if (this.pendingStream) {
			const s = this.pendingStream;
			this.pendingStream = null;
			this.bindRemoteTrack(s);
		}
	}

	prepareAudio () {
		if (!this.audioCtx || this.audioCtx.state === 'closed') {
			this.audioCtx = new AudioContext();
		}
		if (this.audioCtx.state === 'suspended') {
			this.audioCtx.resume().catch(() => {});
		}
	}

	closeAudioCtx () {
		if (this.audioCtx) {
			this.audioCtx.close();
			this.audioCtx = null;
		}
	}

	openSession (serverUrl, pollIntervalMs) {
		this.janus = new Janus({
			server: serverUrl,
			success: () => {
				this.attachListPlugin(pollIntervalMs);
			},
			error: () => {
				this.onConnection(false);
			},
			destroyed: () => {
				this.onConnection(false);
			},
		});
	}

	attachListPlugin (pollIntervalMs) {
		this.backgroundPollMs = pollIntervalMs;
		if (this.janus) {
			this.janus.attach({
				plugin: 'janus.plugin.streaming',
				success: (handle) => {
					this.listHandle = handle;
					this.onConnection(true);
					this.refreshList();
					this.startListPoll(pollIntervalMs);
				},
				error: () => {
					this.onConnection(false);
				},
			});
		}
	}

	startListPoll (ms) {
		this.stopListPoll();
		this.backgroundPollMs = ms;
		this.listPollTimer = setInterval(() => this.refreshList(), ms);
	}

	stopListPoll () {
		if (this.listPollTimer) {
			clearInterval(this.listPollTimer);
			this.listPollTimer = null;
		}
	}

	startFocusedPoll (id, ms) {
		if (ms === undefined) ms = FOCUSED_POLL_MS;
		this.stopFocusedPoll();
		this.focusedPollId = id;
		this.refreshFocused(id);
		this.focusedPollTimer = setInterval(() => this.refreshFocused(id), ms);
	}

	stopFocusedPoll () {
		if (this.focusedPollTimer) {
			clearInterval(this.focusedPollTimer);
			this.focusedPollTimer = null;
		}
		this.focusedPollId = null;
	}

	refreshFocused (id) {
		if (!this.listHandle) return;
		this.listHandle.send({
			message: { request: 'info', id },
			success: (result) => {
				const info = result && result.info;
				if (!info) {
					this.stopFocusedPoll();
					this.refreshList();
					return;
				}
				const stream = normalizeListItem(info);
				if (!stream) return;

				const prev = this.knownIds.get(id);
				this.knownIds.set(id, stream);

				if (!prev) {
					this.onLifecycle('created', stream);
				} else if (stream.metadataRaw !== prev.metadataRaw) {
					this.onLifecycle('metadata', stream);
				}

				this.onList(Array.from(this.knownIds.values()));
			},
		});
	}

	refreshList () {
		if (!this.listHandle) return;
		this.listHandle.send({
			message: { request: 'list' },
			success: (result) => {
				const raw = result && result.list ? result.list : [];
				const streams = raw.map(normalizeListItem).filter((s) => s !== null);
				this.diffAndNotify(streams);
				this.onList(streams);
			},
		});
	}

	diffAndNotify (streams) {
		const nextIds = new Set(streams.map((s) => s.id));

		for (const s of streams) {
			const prev = this.knownIds.get(s.id);
			if (!prev) {
				this.onLifecycle('created', s);
			} else if (s.metadataRaw !== prev.metadataRaw) {
				this.onLifecycle('metadata', s);
			}
		}

		for (const [id, prev] of this.knownIds) {
			if (!nextIds.has(id)) {
				this.onLifecycle('destroyed', prev);
				if (this.watchHandle && this.watchingId === id) {
					this.stopWatch();
				}
			}
		}

		this.knownIds.clear();
		for (const s of streams) {
			this.knownIds.set(s.id, s);
		}
	}

	startWatch (stream, pin) {
		if (!this.janus) return;

		this.stopWatch();
		this.watchingId = stream.id;

		this.stopListPoll();
		this.startFocusedPoll(stream.id, FOCUSED_POLL_MS);

		this.janus.attach({
			plugin: 'janus.plugin.streaming',
			success: (handle) => {
				this.watchHandle = handle;
				let remoteStreamBound = false;

				const onRemote = (remoteStream) => {
					if (remoteStreamBound) return;
					remoteStreamBound = true;
					this.bindRemoteTrack(remoteStream);
				};

				// Legacy Janus API (Plan-B SDP) — kept for older server versions
				handle.onremotestream = onRemote;

				// Current Janus API (Unified Plan) — onremotestream is no longer fired
				handle.ontrack = (track, _mid, on) => {
					if (!on || track.kind !== 'audio') return;
					const existing = handle.webrtcStuff && handle.webrtcStuff.remoteStream;
					onRemote(existing || new MediaStream([track]));
				};

				handle.onmessage = (msg, jsep) => {
					if (msg.error) {
						return;
					}
					if (msg.streaming === 'edited' && typeof msg.metadata === 'string') {
						const live = parseMetadata(msg.metadata);
						const stored = this.knownIds.get(stream.id);
						if (stored) {
							stored.metadata = live;
							stored.metadataRaw = msg.metadata;
							this.onLifecycle('metadata', stored);
						}
					}
					if (jsep) {
						handle.createAnswer({
							jsep,
							media: {
								audioSend: false,
								videoSend: false,
								audioRecv: true,
								videoRecv: false,
							},
							success: (answerJsep) => {
								const rs = handle.webrtcStuff && handle.webrtcStuff.remoteStream;
								const rsTracks = rs && typeof rs.getTracks === 'function' ? rs.getTracks() : [];
								if (rs && rsTracks.length > 0) {
									// Old Janus (Plan-B): remoteStream already contains tracks
									onRemote(rs);
								} else {
									// New Janus (Unified Plan): track lives on the RTCRtpReceiver
									const pc = handle.webrtcStuff && handle.webrtcStuff.pc;
									const receiver = pc && pc.getReceivers().find((r) => r.track && r.track.kind === 'audio');
									if (receiver) onRemote(new MediaStream([receiver.track]));
								}
								handle.send({
									message: { request: 'start' },
									jsep: answerJsep,
								});
							},
							error: () => {},
						});
					}
				};

				handle.oncleanup = () => {
					this.stopMeter();
					if (this.remoteAudio) {
						this.remoteAudio.srcObject = null;
					}
				};

				const body = { request: 'watch', id: stream.id };
				if (pin) body.pin = pin;

				handle.send({
					message: body,
				});
			},
			error: () => {
				this.watchingId = null;
				this.watchingId = null;
			},
		});
	}

	bindRemoteTrack (stream) {
		if (!this.remoteAudio) {
			this.pendingStream = stream;
			return;
		}
		this.remoteAudio.muted = true;
		this.remoteAudio.srcObject = stream;
		this.remoteAudio
			.play()
			.then(() => {
				this.remoteAudio.muted = false;
			})
			.catch(() => {
				this.remoteAudio.muted = false;
			});
		this.startMeter(stream);
	}

	startMeter (stream) {
		if (!this.levelCanvas) return;
		this.stopMeter();
		if (!this.audioCtx || this.audioCtx.state === 'closed') {
			this.audioCtx = new AudioContext();
		}
		if (this.audioCtx.state === 'suspended') {
			this.audioCtx.resume().catch(() => {});
		}
		const src = this.audioCtx.createMediaStreamSource(stream);
		this.analyser = this.audioCtx.createAnalyser();
		this.analyser.fftSize = 4096;
		this.analyser.smoothingTimeConstant = 0;
		src.connect(this.analyser);

		const inputBuf = new Float32Array(this.analyser.fftSize);
		const ctx = this.levelCanvas.getContext('2d');
		if (!ctx) return;

		const sampleRate = this.audioCtx.sampleRate;
		const decimation = Math.max(1, Math.round(sampleRate / 800));
		let audioSampleSerial = 0;
		let lastFrameTime = this.audioCtx.currentTime;
		let pointBuf = new Float32Array(0);
		let pointCount = 0;
		let pointStart = 0;

		const ensureBuf = (w) => {
			if (pointBuf.length !== w) {
				pointBuf = new Float32Array(w);
				pointCount = 0;
				pointStart = 0;
			}
		};

		const pushPoint = (v) => {
			const cap = pointBuf.length;
			if (cap === 0) return;
			if (pointCount < cap) {
				pointBuf[(pointStart + pointCount) % cap] = v;
				pointCount++;
			} else {
				pointBuf[pointStart] = v;
				pointStart = (pointStart + 1) % cap;
			}
		};

		const draw = () => {
			if (!this.analyser || !this.levelCanvas || !this.audioCtx) return;

			const cssW = this.levelCanvas.clientWidth | 0;
			const cssH = this.levelCanvas.clientHeight | 0;
			if (cssW > 0 && cssH > 0) {
				if (this.levelCanvas.width !== cssW) this.levelCanvas.width = cssW;
				if (this.levelCanvas.height !== cssH) this.levelCanvas.height = cssH;
			}
			const w = this.levelCanvas.width;
			const h = this.levelCanvas.height;
			ensureBuf(w);

			const now = this.audioCtx.currentTime;
			const dt = Math.max(0, now - lastFrameTime);
			lastFrameTime = now;
			const newRaw = Math.max(0, Math.floor(dt * sampleRate));

			this.analyser.getFloatTimeDomainData(inputBuf);
			const sliceLen = Math.min(inputBuf.length, Math.max(1, newRaw));
			const sliceStart = inputBuf.length - sliceLen;
			for (let i = sliceStart; i < inputBuf.length; i++) {
				audioSampleSerial++;
				if (audioSampleSerial % decimation === 0) {
					pushPoint(inputBuf[i]);
				}
			}

			ctx.clearRect(0, 0, w, h);
			if (pointCount < 2) {
				this.meterRaf = requestAnimationFrame(draw);
				return;
			}
			ctx.strokeStyle = '#a51717';
			ctx.lineWidth = 2;
			ctx.lineCap = 'round';
			ctx.lineJoin = 'round';
			ctx.shadowColor = 'rgba(165, 23, 23, 0.45)';
			ctx.shadowBlur = 2;

			const cy = h / 2;
			const xStart = w - pointCount;
			ctx.beginPath();
			for (let i = 0; i < pointCount; i++) {
				const v = pointBuf[(pointStart + i) % pointBuf.length];
				const x = xStart + i;
				const y = cy - v * cy;
				if (i === 0) ctx.moveTo(x, y);
				else ctx.lineTo(x, y);
			}
			ctx.stroke();
			ctx.shadowBlur = 0;

			this.meterRaf = requestAnimationFrame(draw);
		};
		draw();
	}

	stopMeter () {
		if (this.meterRaf) cancelAnimationFrame(this.meterRaf);
		this.meterRaf = 0;
		this.analyser = null;
	}
}

function normalizeMetadataRaw (item) {
	const m = item && item.metadata;
	if (typeof m === 'string' && m.length > 0) return m;
	if (m && typeof m === 'object') {
		try {
			return JSON.stringify(m);
		} catch {
			return null;
		}
	}
	return null;
}

function normalizeListItem (item) {
	const id = Number(item.id);
	if (!Number.isFinite(id) || id <= 0) return null;
	const metadataRaw = normalizeMetadataRaw(item);
	return {
		id,
		description: (item.description && item.description.trim()) || 'Stream ' + id,
		type: item.type || 'unknown',
		enabled: item.enabled !== false,
		metadata: parseMetadata(metadataRaw),
		metadataRaw,
	};
}

const MAC_BRACKET_RE = /\s*\[(?:[0-9A-Fa-f]{2}[:-]){2,5}[0-9A-Fa-f]{2}\]\s*$/;

/**
 * @param {string | undefined} raw
 * @returns {string}
 */
export function cleanDescription (raw) {
	return (raw || '').replace(MAC_BRACKET_RE, '').trim();
}
